import React from 'react';
import { motion } from 'motion/react';
import {
  ThumbsUp,
  ThumbsDown,
  Copy,
  Check,
  ExternalLink,
  Sparkles,
  Bot,
  Zap,
  MessageSquare,
  BarChart3,
  Clock,
  Square,
  SquarePen,
  AlertTriangle,
  Brain,
  FileCode,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock } from '../ui/CodeBlock';
import VantiChart from './VantiChart';
import VantiVegaChart from './VantiVegaChart';
import VantiTable from './VantiTable';
import VantiSummary from './VantiSummary';
import VantiQuestion from './VantiQuestion';
import ScopePickButtons from './ScopePickButtons';
import InlineScopePicker from './InlineScopePicker';
import ScopeChangeIndicator from './ScopeChangeIndicator';
import GenericIndicator from './GenericIndicator';
import { stripPlaceholders } from './chatExport';
import {
  bubbleUser,
  bubbleAssistant,
  chatLinkClass,
  chatListClass,
  followUpChipClass,
  ragPillClass,
  ragFileIconClass,
  thinkingText,
  ghostIconButton,
} from './chatViewTheme';

const markdownComponents = {
  code({ className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    if (language === 'vanti-chart' || language === 'vanti-question' || language === 'vanti-table' || language === 'vanti-summary') return null;
    if (language === 'vanti-vega') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-md border border-dark-border bg-dark-elevated px-2.5 py-1 text-xs text-slate-500">
          <span aria-hidden>📈</span>
          Rendering chart…
        </span>
      );
    }
    const inline = !language && !String(children).includes('\n');
    return (
      <CodeBlock language={language} inline={inline} {...props}>
        {children}
      </CodeBlock>
    );
  },
  pre({ children }) {
    return <>{children}</>;
  },
  p({ children }) {
    return (
      <motion.div layout={false} className="mb-2 last:mb-0 leading-relaxed">
        {children}
      </motion.div>
    );
  },
  ul({ children }) {
    return <ul className={chatListClass}>{children}</ul>;
  },
  ol({ children }) {
    return <ol className="mb-2 list-inside list-decimal space-y-0.5">{children}</ol>;
  },
  li({ children }) {
    return <li className="text-sm">{children}</li>;
  },
  strong({ children }) {
    return <strong className="font-semibold">{children}</strong>;
  },
  a({ href, children }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={chatLinkClass}>
        {children}
      </a>
    );
  },
};

const MODE_ICONS = { quick: Zap, interactive: MessageSquare, analyze: BarChart3 };

export default function ChatMessage({
  message: m,
  loading,
  feedbackState,
  copiedResponseId,
  reduceMotion,
  streamingStatus = '',
  onFeedback,
  onCopyResponse,
  onAnswer,
  onScopeSelect,
  onInlineScopeSelect,
  onNavigate,
  onModeChange,
  onSetInput,
  onFollowUp,
  onRetryFailed,
}: any) {
  if (m.type === 'scope_change') {
    return <ScopeChangeIndicator scope={m.scope} reduceMotion={reduceMotion} />;
  }
  if (m.type === 'agent_change') {
    return (
      <GenericIndicator
        icon={Bot}
        label={`Agent: ${m.agentLabel}`}
        variant="purple"
        reduceMotion={reduceMotion}
      />
    );
  }
  if (m.type === 'mode_change') {
    const Icon = MODE_ICONS[m.modeId] || Zap;
    return (
      <GenericIndicator
        icon={Icon}
        label={`Mode: ${m.modeLabel}`}
        variant="default"
        reduceMotion={reduceMotion}
      />
    );
  }
  if (m.type === 'conversation_resumed') {
    return (
      <GenericIndicator
        icon={Clock}
        label={`Resumed · ${m.timeLabel}`}
        variant="default"
        reduceMotion={reduceMotion}
      />
    );
  }
  if (m.type === 'new_conversation') {
    return (
      <GenericIndicator
        icon={SquarePen}
        label="New conversation"
        variant="default"
        reduceMotion={reduceMotion}
      />
    );
  }
  if (m.type === 'response_stopped') {
    return (
      <GenericIndicator
        icon={Square}
        label="Response stopped"
        variant="default"
        reduceMotion={reduceMotion}
      />
    );
  }
  if (m.type === 'message_failed') {
    return (
      <GenericIndicator
        icon={AlertTriangle}
        label={m.errorMessage || 'Message failed to send'}
        sublabel={m.errorSublabel}
        variant="amber"
        reduceMotion={reduceMotion}
        action={
          m.retryPayload
            ? {
                label: 'Retry',
                onClick: () => onRetryFailed(m.id, m.retryPayload),
              }
            : undefined
        }
      />
    );
  }

  const feedbackHover = reduceMotion ? {} : { whileHover: { scale: 1.1 } };
  const actionButtonMotion = reduceMotion
    ? {}
    : { whileHover: { scale: 1.03 }, whileTap: { scale: 0.97 } };

  const showStreamingStatus = m.streaming && !m.content && streamingStatus;

  return (
    <motion.div
      layout={reduceMotion ? false : undefined}
      className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}
    >
      <motion.div
        layout={false}
        className={`max-w-[85%] rounded-2xl text-sm ${
          m.role === 'user' ? bubbleUser : bubbleAssistant
        }`}
      >
        {m.role === 'assistant' ? (
          showStreamingStatus ? (
            <div className={`flex items-center gap-2 px-4 py-2.5 text-sm ${thinkingText}`}>
              <span className="animate-pulse">{streamingStatus}</span>
            </div>
          ) : (
            <div className="px-4 py-2.5 text-sm leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as any}>
                {stripPlaceholders(m.content)}
              </ReactMarkdown>
              {m.streaming ? (
                <span className="inline-block animate-pulse text-vivid-purple" aria-hidden>
                  ▋
                </span>
              ) : null}
            </div>
          )
        ) : (
          <motion.div layout={false} className="whitespace-pre-wrap break-words px-4 py-2.5">
            {m.content}
          </motion.div>
        )}
      </motion.div>

      {m.role === 'assistant' && m.charts?.length > 0 && (
        <motion.div layout={false} className="mt-2 max-w-[85%] space-y-2">
          {m.charts.map((chart, ci) => (
            <VantiChart key={ci} spec={chart} />
          ))}
        </motion.div>
      )}

      {m.role === 'assistant' && m.vegaCharts?.length > 0 && (
        <motion.div layout={false} className="mt-2 max-w-[85%] space-y-2">
          {m.vegaCharts.map((spec: object, vi: number) => (
            <VantiVegaChart key={vi} spec={spec} />
          ))}
        </motion.div>
      )}

      {m.role === 'assistant' && m.tables?.length > 0 && (
        <motion.div layout={false} className="mt-2 max-w-[85%] space-y-2">
          {m.tables.map((spec: any, ti: number) => (
            <VantiTable key={ti} spec={spec} />
          ))}
        </motion.div>
      )}

      {m.role === 'assistant' && m.summaries?.length > 0 && (
        <motion.div layout={false} className="mt-2 max-w-[85%] space-y-2">
          {m.summaries.map((spec: any, si: number) => (
            <VantiSummary key={si} spec={spec} />
          ))}
        </motion.div>
      )}

      {m.role === 'assistant' && m.questions?.length > 0 && (
        <motion.div layout={false} className="mt-1 max-w-[85%]">
          {m.questions.map((q, qi) => (
            <VantiQuestion
              key={qi}
              question={q}
              disabled={loading}
              onAnswer={(answer) => onAnswer(m.id, qi, answer)}
            />
          ))}
        </motion.div>
      )}

      {m.role === 'assistant' && m.memoryUsed && !m.streaming && (
        <motion.div layout={false} className="mt-1 flex max-w-[85%] items-center gap-1">
          <button
            type="button"
            className="flex items-center gap-1 rounded-full border border-vivid-green-cyan/20 bg-vivid-green-cyan/10 px-2 py-0.5 text-[10px] font-medium text-vivid-green-cyan hover:bg-vivid-green-cyan/10"
            title="This response used your saved memories"
            aria-label="Memory was used for this response"
          >
            <Brain className="h-3 w-3" aria-hidden />
            Used memory
          </button>
        </motion.div>
      )}

      {m.role === 'assistant' && m.ragSources?.length > 0 && !m.streaming && (
        <motion.div layout={false} className="mt-1.5 flex max-w-[85%] flex-wrap gap-1.5">
          {m.ragSources.map((src) => (
            <span key={src} className={ragPillClass} title={src}>
              <FileCode className={`${ragFileIconClass} h-2.5 w-2.5 shrink-0`} aria-hidden />
              {src.length > 32 ? `…${src.slice(-29)}` : src}
            </span>
          ))}
        </motion.div>
      )}

      {m.role === 'assistant' && m.followUps?.length > 0 && !m.streaming && !loading && (
        <motion.div
          layout={false}
          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.3 }}
          className="mt-2 flex max-w-[85%] flex-wrap gap-1.5"
        >
          {m.followUps.map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onFollowUp(q)}
              className={followUpChipClass}
            >
              {q}
            </button>
          ))}
        </motion.div>
      )}

      {m.role === 'assistant' && !m.streaming && stripPlaceholders(m.content) && (
        <motion.div layout={false} className="mt-1.5 flex max-w-[85%] items-center gap-1">
          <motion.button
            type="button"
            onClick={() => onCopyResponse(m.id, m.content)}
            className={`rounded-lg p-1.5 transition-colors ${
              copiedResponseId === m.id
                ? 'bg-vivid-green-cyan/10 text-vivid-green-cyan'
                : `${ghostIconButton} p-1.5`
            }`}
            aria-label={copiedResponseId === m.id ? 'Copied' : 'Copy response'}
            title={copiedResponseId === m.id ? 'Copied' : 'Copy response'}
            {...feedbackHover}
          >
            {copiedResponseId === m.id ? (
              <Check className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden />
            )}
          </motion.button>
          {m.turnId ? (
            <>
              <motion.button
                type="button"
                disabled={Boolean(feedbackState[m.id])}
                onClick={() => onFeedback(m.id, m.turnId, 'up')}
                className={`rounded-lg p-1.5 transition-colors ${
                  feedbackState[m.id] === 'up'
                    ? 'bg-vivid-green-cyan/10 text-vivid-green-cyan'
                    : `${ghostIconButton} p-1.5`
                }`}
                aria-label="Helpful"
                {...feedbackHover}
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </motion.button>
              <motion.button
                type="button"
                disabled={Boolean(feedbackState[m.id])}
                onClick={() => onFeedback(m.id, m.turnId, 'down')}
                className={`rounded-lg p-1.5 transition-colors ${
                  feedbackState[m.id] === 'down'
                    ? 'bg-vivid-red/10 text-vivid-red'
                    : `${ghostIconButton} p-1.5`
                }`}
                aria-label="Not helpful"
                {...feedbackHover}
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </motion.button>
            </>
          ) : null}
        </motion.div>
      )}

      {m.role === 'assistant' && m.scopePicks?.length > 0 && !m.scopeResolved && (
        <ScopePickButtons
          picks={m.scopePicks}
          disabled={loading}
          onSelect={(pick) => onScopeSelect(m.id, pick, m.originalQuestion)}
        />
      )}

      {m.role === 'assistant' &&
        m.clarificationOnly &&
        !m.scopePicks?.length &&
        !m.scopeResolved && (
          <motion.div layout={false} className="max-w-[85%]">
            <InlineScopePicker
              onSelect={(item) => onInlineScopeSelect(m.id, item, m.originalQuestion)}
            />
          </motion.div>
        )}

      {m.role === 'assistant' && m.quickActions?.length > 0 && (
        <motion.div layout={false} className="mt-3 flex max-w-[85%] flex-wrap gap-2">
          {m.quickActions.map((action, j) => {
            if (action.type === 'navigate' && action.path) {
              return (
                <motion.button
                  key={j}
                  type="button"
                  {...actionButtonMotion}
                  onClick={() => onNavigate(action.path)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-vivid-purple/30 bg-vivid-purple/8 px-3 py-1.5 text-xs font-medium text-vivid-purple transition-colors hover:bg-vivid-purple/15"
                >
                  <ExternalLink className="h-3 w-3" aria-hidden />
                  {action.label}
                </motion.button>
              );
            }
            if (action.type === 'agent_mode') {
              return (
                <motion.button
                  key={j}
                  type="button"
                  {...actionButtonMotion}
                  onClick={() => {
                    if (action.mode) onModeChange(action.mode);
                    if (action.prompt) onSetInput(action.prompt);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-vivid-green-cyan/30 bg-vivid-green-cyan/8 px-3 py-1.5 text-xs font-medium text-vivid-green-cyan transition-colors hover:bg-vivid-green-cyan/15"
                >
                  <Sparkles className="h-3 w-3" aria-hidden />
                  {action.label}
                </motion.button>
              );
            }
            if (action.path) {
              return (
                <motion.button
                  key={j}
                  type="button"
                  {...actionButtonMotion}
                  onClick={() => onNavigate(action.path)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-vivid-purple/30 bg-vivid-purple/8 px-3 py-1.5 text-xs font-medium text-vivid-purple transition-colors hover:bg-vivid-purple/15"
                >
                  <ExternalLink className="h-3 w-3" aria-hidden />
                  {action.label}
                </motion.button>
              );
            }
            return null;
          })}
        </motion.div>
      )}
    </motion.div>
  );
}
